/**
 * 表示言語ごとの日付・金額の表記 (admin-english-toggle-20260924 phase 2 / ADR-081 決定 6)。
 *
 * 規則 (phase 2 の各 slice はここの関数を使い、画面ごとに書き直さない):
 * - **数字だけの表記は変えない** (`YYYY/MM/DD`・`M/D`・`HH:MM`)。データと照合するときに書式が
 *   変わらない方を優先する。変えるのは日本語の語 (年・月・日・曜日・「分」・「前」) を含む表記だけ。
 * - **暦日は JST で決める**。端末のタイムゾーンを混ぜない (英語の整形は JST の暦日を UTC の 0 時
 *   として `timeZone: 'UTC'` で整形する)。
 * - **金額**: 日本語表示は今の表記 (画面ごとの既存 util) のまま。英語表示は `formatYen(amount, 'en')`
 *   の「¥12,000」(区切りは同じ、負数は「-¥1,000」)。「円」の字は英語表示の利用者が読めないため
 *   (ADR-081 決定 6 の追記)。CSV / PDF / 依頼者へ送る文の金額は日本語のまま。
 * - **複数形**: 英語は「Tasks: 3」のように名詞と数を分けて、単数 / 複数の出し分けを避ける
 *   (辞書に `{count}` を 1 つだけ持つ文言を置き、`task(s)` のような書き方はしない)。
 *   どうしても文にするときは、辞書に単数 (`...One`) と複数 (`...Other`) の 2 キーを置き、
 *   `count === 1` で選ぶ (phase 1 のタスクの KPI 帯と同じ)。
 *
 * 日本語の表記は、各画面の既存 util (例: `formatTaskDueLabel` / `formatBudgetMonth`) が正本。
 * ここでは英語の形と、両言語で同じ規則にしたい小さな表記 (月の目盛・経過時間・金額) を持つ。
 */
import type { Locale } from './types';

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const MS_PER_MINUTE = 60 * 1000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const YEAR_MONTH_RE = /^(\d{4})-(\d{2})$/;

/** 暦日 (JST)。月は 1〜12。 */
export interface CalendarDay {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

const EN_MONTH_DAY_WEEKDAY = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});
const EN_MONTH_DAY_WEEKDAY_YEAR = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const EN_MONTH_DAY = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
});
const EN_MONTH_DAY_YEAR = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const EN_YEAR_MONTH = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  year: 'numeric',
});
const EN_MONTH_SHORT = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short' });
const EN_WEEKDAY_SHORT = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short' });

const JA_WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'] as const;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function utcMidnight(day: CalendarDay): Date {
  return new Date(Date.UTC(day.year, day.month - 1, day.day));
}

function isValidDay(day: CalendarDay): boolean {
  const d = utcMidnight(day);
  return (
    d.getUTCFullYear() === day.year &&
    d.getUTCMonth() === day.month - 1 &&
    d.getUTCDate() === day.day
  );
}

/** タイムゾーンの無い日時 (`2026-09-08T23:30` / `2026-09-08 23:30:00`)。JST として読む。 */
const NAIVE_DATE_TIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3})\d*)?)?$/;

/**
 * 値を瞬間 (epoch ms) にする。解釈できない値・Date の範囲外は null。
 * - タイムゾーン付き (`Z` / `+09:00`) の文字列・`Date`・epoch ms はそのまま
 * - **タイムゾーンの無い日時の文字列は JST として読む** (`Date.parse` は端末のタイムゾーンで読むため、
 *   セブ (UTC+8) の端末では 1 時間ずれて暦日が変わることがある)
 */
function toInstantMs(value: string | Date | number): number | null {
  let ms: number;
  if (value instanceof Date) ms = value.getTime();
  else if (typeof value === 'number') ms = value;
  else {
    const naive = NAIVE_DATE_TIME_RE.exec(value);
    if (naive) {
      const day = { year: Number(naive[1]), month: Number(naive[2]), day: Number(naive[3]) };
      const hour = Number(naive[4]);
      const minute = Number(naive[5]);
      const second = Number(naive[6] ?? 0);
      // 実在しない日付・範囲外の時分秒 (13 月・25 時など) は繰り上げずに解釈できない値とする
      if (!isValidDay(day) || hour > 23 || minute > 59 || second > 59) return null;
      ms =
        Date.UTC(
          day.year,
          day.month - 1,
          day.day,
          hour,
          minute,
          second,
          Number((naive[7] ?? '0').padEnd(3, '0')),
        ) - JST_OFFSET_MS;
    } else {
      ms = Date.parse(value);
    }
  }
  if (!Number.isFinite(ms)) return null;
  // Date の範囲外 (±8.64e15 ms) は Invalid Date になる。JST へずらした後も含めて確かめる
  if (
    Number.isNaN(new Date(ms).getTime()) ||
    Number.isNaN(new Date(ms + JST_OFFSET_MS).getTime())
  ) {
    return null;
  }
  return ms;
}

/**
 * 値を JST の暦日にする。
 * - `YYYY-MM-DD` はその暦日そのもの (時差で動かさない)
 * - それ以外の文字列 (ISO8601 の時刻付き) / `Date` / epoch ms は、その瞬間の JST の暦日
 *   (タイムゾーンの無い日時は JST として読む)
 * - 解釈できない値・Date の範囲外は null
 */
export function toJstCalendarDay(
  value: string | Date | number | null | undefined,
): CalendarDay | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const m = DATE_KEY_RE.exec(value);
    if (m) {
      const day = { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
      return isValidDay(day) ? day : null;
    }
  }
  const ms = toInstantMs(value);
  if (ms === null) return null;
  const jst = new Date(ms + JST_OFFSET_MS);
  return { year: jst.getUTCFullYear(), month: jst.getUTCMonth() + 1, day: jst.getUTCDate() };
}

/** その瞬間の JST の時刻 `HH:MM` (数字だけなので両言語で同じ)。解釈できない値は null。 */
export function formatJstTime(value: string | Date | number | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null;
  const ms = toInstantMs(value);
  if (ms === null) return null;
  const jst = new Date(ms + JST_OFFSET_MS);
  return `${pad2(jst.getUTCHours())}:${pad2(jst.getUTCMinutes())}`;
}

/** `now` の JST の年と同じ年か。 */
function isCurrentJstYear(day: CalendarDay, now: Date): boolean {
  return day.year === toJstCalendarDay(now)?.year;
}

/**
 * 曜日付きの月日。「9月8日（火）」の英語の形 →「Tue, Sep 8」。JST の当年以外は年も付ける
 * (「Tue, Jan 5, 2027」)。解釈できない値は null (呼び出し側で「期限なし」などを出す)。
 */
export function formatMonthDayWeekdayEn(
  value: string | Date | number | null | undefined,
  now: Date = new Date(),
): string | null {
  const day = toJstCalendarDay(value);
  if (!day) return null;
  const format = isCurrentJstYear(day, now) ? EN_MONTH_DAY_WEEKDAY : EN_MONTH_DAY_WEEKDAY_YEAR;
  return format.format(utcMidnight(day));
}

/**
 * 月日。「9月1日」の英語の形 →「Sep 1」。`withYear` が `auto` なら JST の当年以外で年を付ける
 * (「Jan 5, 2027」)。`never` は常に省く (年が文脈で明らかなとき)。
 */
export function formatMonthDayEn(
  value: string | Date | number | null | undefined,
  options: { now?: Date; withYear?: 'auto' | 'never' } = {},
): string | null {
  const day = toJstCalendarDay(value);
  if (!day) return null;
  const withYear =
    (options.withYear ?? 'auto') === 'auto' && !isCurrentJstYear(day, options.now ?? new Date());
  return (withYear ? EN_MONTH_DAY_YEAR : EN_MONTH_DAY).format(utcMidnight(day));
}

/**
 * 年月。「2026年8月分」「2026年8月」の英語の形 →「Aug 2026」。
 * `YYYY-MM` / `YYYY-MM-DD` / 瞬間 (JST の暦月) を受ける。解釈できない値は null。
 */
export function formatYearMonthEn(value: string | Date | number | null | undefined): string | null {
  if (typeof value === 'string') {
    const m = YEAR_MONTH_RE.exec(value);
    if (m) {
      const month = Number(m[2]);
      if (month < 1 || month > 12) return null;
      return EN_YEAR_MONTH.format(utcMidnight({ year: Number(m[1]), month, day: 1 }));
    }
  }
  const day = toJstCalendarDay(value);
  return day ? EN_YEAR_MONTH.format(utcMidnight({ ...day, day: 1 })) : null;
}

/**
 * 年月の見出し (表示言語別)。日本語「2026年8月」/ 英語「Aug 2026」。
 * `YYYY-MM` / `YYYY-MM-DD` / 瞬間 (JST の暦月) を受ける。解釈できない値は null。
 */
export function formatYearMonth(
  locale: Locale,
  value: string | Date | number | null | undefined,
): string | null {
  if (locale !== 'ja') return formatYearMonthEn(value);
  if (typeof value === 'string') {
    const m = YEAR_MONTH_RE.exec(value);
    if (m) {
      const month = Number(m[2]);
      return month >= 1 && month <= 12 ? `${Number(m[1])}年${month}月` : null;
    }
  }
  const day = toJstCalendarDay(value);
  return day ? `${day.year}年${day.month}月` : null;
}

/**
 * 月日 (表示言語別、年は付けない)。日本語「9月8日」/ 英語「Sep 8」。
 * 年も要る英語の表記は {@link formatMonthDayEn}。解釈できない値は null。
 */
export function formatMonthDay(
  locale: Locale,
  value: string | Date | number | null | undefined,
): string | null {
  if (locale !== 'ja') return formatMonthDayEn(value, { withYear: 'never' });
  const day = toJstCalendarDay(value);
  return day ? `${day.month}月${day.day}日` : null;
}

/** グラフの月の目盛 (月は 1〜12)。日本語「8月」/ 英語「Aug」。範囲外は数字のまま。 */
export function formatMonthTick(locale: Locale, month: number): string {
  if (!Number.isInteger(month) || month < 1 || month > 12) return String(month);
  if (locale === 'ja') return `${month}月`;
  return EN_MONTH_SHORT.format(utcMidnight({ year: 2000, month, day: 1 }));
}

/** 曜日の短い表記 (0 = 日曜)。日本語「火」/ 英語「Tue」。範囲外は空文字。 */
export function formatWeekdayShort(locale: Locale, weekday: number): string {
  if (!Number.isInteger(weekday) || weekday < 0 || weekday > 6) return '';
  if (locale === 'ja') return JA_WEEKDAYS[weekday];
  // 2000-01-02 は日曜日
  return EN_WEEKDAY_SHORT.format(utcMidnight({ year: 2000, month: 1, day: 2 + weekday }));
}

/**
 * 経過時間。日本語「たった今 / 5分前 / 3時間前 / 2日前」、英語「just now / 5m ago / 3h ago / 2d ago」。
 * 未来の時刻・解釈できない値は「たった今 / just now」に倒さず null を返す (呼び出し側で日時を出す)。
 */
export function formatRelativeTime(
  locale: Locale,
  value: string | Date | number | null | undefined,
  now: Date = new Date(),
): string | null {
  if (value === null || value === undefined || value === '') return null;
  const ms = toInstantMs(value);
  if (ms === null) return null;
  const diffMinutes = Math.floor((now.getTime() - ms) / MS_PER_MINUTE);
  if (diffMinutes < 0) return null;
  const ja = locale === 'ja';
  if (diffMinutes < 1) return ja ? 'たった今' : 'just now';
  if (diffMinutes < MINUTES_PER_HOUR) return ja ? `${diffMinutes}分前` : `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / MINUTES_PER_HOUR);
  if (diffHours < HOURS_PER_DAY) return ja ? `${diffHours}時間前` : `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / HOURS_PER_DAY);
  return ja ? `${diffDays}日前` : `${diffDays}d ago`;
}

/**
 * 金額 (整数円)。日本語「12,000円」/ 英語「¥12,000」、負数は「-12,000円」/「-¥12,000」。
 * 区切り (3 桁のカンマ) は両言語で同じ。数値でない値は「-」。
 *
 * 日本語表示で画面ごとに表記が違う箇所 (「12,000 円」「¥12,000」) は、既存の util の出力を
 * そのまま使い、英語表示だけこの関数の `en` を使う (日本語の見た目を変えない)。
 */
export function formatYen(amount: number | null | undefined, locale: Locale): string {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return '-';
  const digits = Math.abs(amount).toLocaleString('ja-JP');
  const sign = amount < 0 ? '-' : '';
  return locale === 'ja' ? `${sign}${digits}円` : `${sign}¥${digits}`;
}
